import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Database, Eye, UserCheck, Trash2, Mail, Globe, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const PrivacyPolicy = () => {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                <span className="font-heading font-bold text-xl text-primary-foreground">M</span>
              </div>
              <span className="font-heading font-bold text-xl">MOTO SOCIAL</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {isFr ? 'Retour à l\'accueil' : 'Back to home'}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
              {isFr ? 'Politique de Confidentialité' : 'Privacy Policy'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {isFr ? 'Conformité RGPD' : 'GDPR Compliant'}
            </p>
          </div>
        </div>

        {/* RGPD Badge */}
        <Card className="mb-8 border-green-500/20 bg-green-500/5">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="font-medium text-green-500">
                {isFr ? 'Conforme au RGPD' : 'GDPR Compliant'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isFr 
                  ? 'Règlement Général sur la Protection des Données (UE) 2016/679'
                  : 'General Data Protection Regulation (EU) 2016/679'}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-8">
          {/* Introduction */}
          <Card>
            <CardHeader>
              <CardTitle>{isFr ? 'Introduction' : 'Introduction'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? "Moto Social s'engage à protéger la vie privée de ses utilisateurs. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD)."
                  : "Moto Social is committed to protecting the privacy of its users. This privacy policy explains how we collect, use, store, and protect your personal data in accordance with the General Data Protection Regulation (GDPR)."}
              </p>
              <p>
                <strong className="text-foreground">{isFr ? 'Responsable du traitement' : 'Data Controller'}:</strong><br />
                Moto Social SASU<br />
                55 rue du Four Saint-François, 66000 Perpignan, France<br />
                Email: <a href="mailto:privacy@motosocial.eu" className="text-primary hover:underline">privacy@motosocial.eu</a>
              </p>
            </CardContent>
          </Card>

          {/* Données collectées */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" />
                {isFr ? 'Données collectées' : 'Data Collected'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p><strong className="text-foreground">{isFr ? 'Données d\'inscription' : 'Registration Data'}:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>{isFr ? 'Nom d\'utilisateur' : 'Username'}</li>
                <li>{isFr ? 'Adresse email' : 'Email address'}</li>
                <li>{isFr ? 'Mot de passe (chiffré)' : 'Password (encrypted)'}</li>
                <li>{isFr ? 'Langue préférée' : 'Preferred language'}</li>
              </ul>

              <p><strong className="text-foreground">{isFr ? 'Données de profil' : 'Profile Data'}:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>{isFr ? 'Photo de profil (optionnel)' : 'Profile picture (optional)'}</li>
                <li>{isFr ? 'Biographie (optionnel)' : 'Biography (optional)'}</li>
                <li>{isFr ? 'Localisation (optionnel)' : 'Location (optional)'}</li>
                <li>{isFr ? 'Informations sur vos motos' : 'Motorcycle information'}</li>
              </ul>

              <p><strong className="text-foreground">{isFr ? 'Données d\'utilisation' : 'Usage Data'}:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>{isFr ? 'Publications et commentaires' : 'Posts and comments'}</li>
                <li>{isFr ? 'Événements créés ou rejoints' : 'Events created or joined'}</li>
                <li>{isFr ? 'Annonces marketplace' : 'Marketplace listings'}</li>
                <li>{isFr ? 'Historique des transactions' : 'Transaction history'}</li>
              </ul>

              <p><strong className="text-foreground">{isFr ? 'Données techniques' : 'Technical Data'}:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>{isFr ? 'Adresse IP' : 'IP address'}</li>
                <li>{isFr ? 'Type de navigateur' : 'Browser type'}</li>
                <li>{isFr ? 'Appareil utilisé' : 'Device used'}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Finalités */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                {isFr ? 'Finalités du traitement' : 'Purpose of Processing'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>{isFr ? 'Vos données sont utilisées pour' : 'Your data is used to'}:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{isFr ? 'Créer et gérer votre compte utilisateur' : 'Create and manage your user account'}</li>
                <li>{isFr ? 'Permettre les interactions sociales (posts, likes, commentaires)' : 'Enable social interactions (posts, likes, comments)'}</li>
                <li>{isFr ? 'Faciliter les transactions sur le marketplace' : 'Facilitate marketplace transactions'}</li>
                <li>{isFr ? 'Vous informer des événements pertinents' : 'Inform you of relevant events'}</li>
                <li>{isFr ? 'Améliorer nos services et votre expérience' : 'Improve our services and your experience'}</li>
                <li>{isFr ? 'Assurer la sécurité de la plateforme' : 'Ensure platform security'}</li>
                <li>{isFr ? 'Respecter nos obligations légales' : 'Comply with our legal obligations'}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Base légale */}
          <Card>
            <CardHeader>
              <CardTitle>{isFr ? 'Base légale du traitement' : 'Legal Basis for Processing'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <ul className="space-y-4">
                <li>
                  <strong className="text-foreground">{isFr ? 'Exécution du contrat' : 'Contract Performance'}:</strong>{' '}
                  {isFr 
                    ? 'Traitement nécessaire pour fournir nos services (création de compte, marketplace, événements).'
                    : 'Processing necessary to provide our services (account creation, marketplace, events).'}
                </li>
                <li>
                  <strong className="text-foreground">{isFr ? 'Consentement' : 'Consent'}:</strong>{' '}
                  {isFr
                    ? 'Pour l\'envoi de newsletters et communications marketing (vous pouvez retirer votre consentement à tout moment).'
                    : 'For sending newsletters and marketing communications (you can withdraw consent at any time).'}
                </li>
                <li>
                  <strong className="text-foreground">{isFr ? 'Intérêt légitime' : 'Legitimate Interest'}:</strong>{' '}
                  {isFr
                    ? 'Pour améliorer nos services et assurer la sécurité de la plateforme.'
                    : 'To improve our services and ensure platform security.'}
                </li>
                <li>
                  <strong className="text-foreground">{isFr ? 'Obligation légale' : 'Legal Obligation'}:</strong>{' '}
                  {isFr
                    ? 'Conservation des données de transaction pour obligations fiscales.'
                    : 'Retention of transaction data for tax obligations.'}
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Vos droits RGPD */}
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-primary" />
                {isFr ? 'Vos droits (RGPD)' : 'Your Rights (GDPR)'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>{isFr ? 'Conformément au RGPD, vous disposez des droits suivants' : 'Under GDPR, you have the following rights'}:</p>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 rounded-lg bg-card border border-border">
                  <p className="font-medium text-foreground mb-1">{isFr ? 'Droit d\'accès' : 'Right of Access'}</p>
                  <p className="text-sm">{isFr ? 'Obtenir une copie de vos données personnelles' : 'Obtain a copy of your personal data'}</p>
                </div>
                <div className="p-4 rounded-lg bg-card border border-border">
                  <p className="font-medium text-foreground mb-1">{isFr ? 'Droit de rectification' : 'Right to Rectification'}</p>
                  <p className="text-sm">{isFr ? 'Corriger vos données inexactes' : 'Correct your inaccurate data'}</p>
                </div>
                <div className="p-4 rounded-lg bg-card border border-border">
                  <p className="font-medium text-foreground mb-1">{isFr ? 'Droit à l\'effacement' : 'Right to Erasure'}</p>
                  <p className="text-sm">{isFr ? 'Demander la suppression de vos données' : 'Request deletion of your data'}</p>
                </div>
                <div className="p-4 rounded-lg bg-card border border-border">
                  <p className="font-medium text-foreground mb-1">{isFr ? 'Droit à la portabilité' : 'Right to Portability'}</p>
                  <p className="text-sm">{isFr ? 'Recevoir vos données dans un format structuré' : 'Receive your data in a structured format'}</p>
                </div>
                <div className="p-4 rounded-lg bg-card border border-border">
                  <p className="font-medium text-foreground mb-1">{isFr ? 'Droit d\'opposition' : 'Right to Object'}</p>
                  <p className="text-sm">{isFr ? 'Vous opposer au traitement de vos données' : 'Object to the processing of your data'}</p>
                </div>
                <div className="p-4 rounded-lg bg-card border border-border">
                  <p className="font-medium text-foreground mb-1">{isFr ? 'Droit à la limitation' : 'Right to Restriction'}</p>
                  <p className="text-sm">{isFr ? 'Limiter le traitement de vos données' : 'Restrict the processing of your data'}</p>
                </div>
              </div>

              <p className="pt-4">
                {isFr
                  ? 'Pour exercer vos droits, contactez-nous à :'
                  : 'To exercise your rights, contact us at:'}{' '}
                <a href="mailto:privacy@motosocial.eu" className="text-primary hover:underline">privacy@motosocial.eu</a>
              </p>
              <p>
                {isFr
                  ? 'Vous pouvez également introduire une réclamation auprès de la CNIL (Commission Nationale de l\'Informatique et des Libertés).'
                  : 'You may also lodge a complaint with your local data protection authority.'}
              </p>
            </CardContent>
          </Card>

          {/* Conservation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-primary" />
                {isFr ? 'Durée de conservation' : 'Data Retention'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <ul className="space-y-2">
                <li>
                  <strong className="text-foreground">{isFr ? 'Données de compte' : 'Account data'}:</strong>{' '}
                  {isFr ? 'Conservées tant que le compte est actif, puis 3 ans après suppression' : 'Kept while account is active, then 3 years after deletion'}
                </li>
                <li>
                  <strong className="text-foreground">{isFr ? 'Données de transaction' : 'Transaction data'}:</strong>{' '}
                  {isFr ? '10 ans (obligation légale)' : '10 years (legal requirement)'}
                </li>
                <li>
                  <strong className="text-foreground">{isFr ? 'Logs de connexion' : 'Connection logs'}:</strong>{' '}
                  {isFr ? '1 an' : '1 year'}
                </li>
                <li>
                  <strong className="text-foreground">{isFr ? 'Cookies' : 'Cookies'}:</strong>{' '}
                  {isFr ? '13 mois maximum' : '13 months maximum'}
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Sécurité */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                {isFr ? 'Sécurité des données' : 'Data Security'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>{isFr ? 'Nous mettons en œuvre les mesures suivantes' : 'We implement the following measures'}:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{isFr ? 'Chiffrement des données en transit (HTTPS/TLS)' : 'Data encryption in transit (HTTPS/TLS)'}</li>
                <li>{isFr ? 'Mots de passe hashés avec bcrypt' : 'Passwords hashed with bcrypt'}</li>
                <li>{isFr ? 'Authentification JWT sécurisée' : 'Secure JWT authentication'}</li>
                <li>{isFr ? 'Paiements sécurisés via Stripe (certifié PCI-DSS)' : 'Secure payments via Stripe (PCI-DSS certified)'}</li>
                <li>{isFr ? 'Accès restreint aux données personnelles' : 'Restricted access to personal data'}</li>
                <li>{isFr ? 'Surveillance continue de la sécurité' : 'Continuous security monitoring'}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                {isFr ? 'Cookies' : 'Cookies'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>{isFr ? 'Nous utilisons les types de cookies suivants' : 'We use the following types of cookies'}:</p>
              <ul className="space-y-2">
                <li>
                  <strong className="text-foreground">{isFr ? 'Cookies essentiels' : 'Essential cookies'}:</strong>{' '}
                  {isFr ? 'Nécessaires au fonctionnement du site (authentification, langue)' : 'Necessary for site operation (authentication, language)'}
                </li>
                <li>
                  <strong className="text-foreground">{isFr ? 'Cookies analytiques' : 'Analytics cookies'}:</strong>{' '}
                  {isFr ? 'Pour comprendre comment vous utilisez notre site (PostHog)' : 'To understand how you use our site (PostHog)'}
                </li>
              </ul>
              <p>
                {isFr
                  ? 'Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.'
                  : 'You can manage your cookie preferences in your browser settings.'}
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                {isFr ? 'Contact' : 'Contact'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground">
              <p>
                {isFr
                  ? 'Pour toute question concernant cette politique de confidentialité ou vos données personnelles :'
                  : 'For any questions regarding this privacy policy or your personal data:'}
              </p>
              <p>
                <strong className="text-foreground">Email:</strong>{' '}
                <a href="mailto:privacy@motosocial.eu" className="text-primary hover:underline">privacy@motosocial.eu</a>
              </p>
              <p>
                <strong className="text-foreground">{isFr ? 'Contact données personnelles' : 'Data Protection Contact'}:</strong><br />
                Kurt<br />
                <a href="mailto:privacy@motosocial.eu" className="text-primary hover:underline">privacy@motosocial.eu</a>
              </p>
            </CardContent>
          </Card>

          {/* Date de mise à jour */}
          <p className="text-sm text-muted-foreground text-center pt-8">
            {isFr ? 'Dernière mise à jour' : 'Last updated'}: {new Date().toLocaleDateString(isFr ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
